// Филтърните функции за клиентски режим.
//
// В сървърен режим НЕ се ползват — там филтърът се вдига до BE през
// `ServerTableBridge.onColumnFiltersChange` и TanStack само пази състоянието.

import type { FilterFn, Row } from "@tanstack/react-table";

/** Стойността, по която колоната се филтрира и фасетира, като низ. */
export const facetValueOf = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    // `dot` и `chip` носят обект — смисълът им е в етикета.
    const label = (value as { label?: unknown }).label;
    if (typeof label === "string") return label;
    const primary = (value as { primary?: unknown }).primary;
    if (typeof primary === "string") return primary;
    return "";
  }
  return String(value);
};

/** Чеклист по стойности — редът минава, ако е в отметнатите. */
export const checklistFilterFn: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown,
) => {
  const selected = Array.isArray(filterValue) ? (filterValue as string[]) : [];
  if (!selected.length) return true;
  return selected.includes(facetValueOf(row.getValue(columnId)));
};

/** Съдържа текст, без значение малки/главни букви. */
export const textFilterFn: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown,
) => {
  const needle = String(filterValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  return facetValueOf(row.getValue(columnId)).toLowerCase().includes(needle);
};

/** Числов диапазон; всяка от двете граници може да липсва. */
export const numberRangeFilterFn: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown,
) => {
  const [min, max] = Array.isArray(filterValue)
    ? (filterValue as [number | null, number | null])
    : [null, null];
  if (min === null && max === null) return true;
  const raw = row.getValue(columnId);
  const value = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(value)) return false;
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
};

/** Диапазон от дати; границите идват като ISO низове от RangePicker-а. */
export const dateRangeFilterFn: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown,
) => {
  const [from, to] = Array.isArray(filterValue)
    ? (filterValue as [string | null, string | null])
    : [null, null];
  if (!from && !to) return true;
  const raw = row.getValue(columnId);
  if (!raw) return false;
  const time = new Date(raw as string).getTime();
  if (!Number.isFinite(time)) return false;
  if (from && time < new Date(from).getTime()) return false;
  if (to && time > new Date(to).getTime()) return false;
  return true;
};

/**
 * Подредба по ПОКАЗАНИЯ текст. Клетките `text`/`dot`/`chip` носят обект, а
 * вградените компаратори на TanStack връщат 0 за обекти — колоната изглежда
 * сортируема, стрелката светва и нищо не се мести.
 */
export const displayTextSortingFn = (
  rowA: Row<unknown>,
  rowB: Row<unknown>,
  columnId: string,
): number =>
  facetValueOf(rowA.getValue(columnId)).localeCompare(
    facetValueOf(rowB.getValue(columnId)),
    undefined,
    { numeric: true, sensitivity: "base" },
  );

/** Празна ли е стойността на филтъра — фунията свети само когато не е. */
export const isEmptyFilterValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((v) => v === null || v === "");
  }
  return false;
};
